import { SCHOOL_YEAR_LABEL, SCHOOL_YEAR_START } from '../data/school-year-2026-2027.ts';
import { uid } from '../lib/id.ts';
import { subjectColor } from '../lib/subjects.ts';
import type { AppData, SyncMeta } from './types.ts';

/** Sync envelope for a freshly created local row. */
export function newMeta(): SyncMeta {
  return { updatedAt: new Date().toISOString(), deletedAt: null, dirty: true };
}

/**
 * The timestamp every seed row carries, and the marker that says nobody has
 * ever touched it.
 *
 * Seeds are demo content, not the teacher's work. Giving them a `Date.now()`
 * stamp and `dirty: true` — which they used to have — meant that installing
 * the app on a second device pushed them straight over the account: the server
 * stamps its own `updated_at` on a push, so the seed `teacher: 'Anso'` won
 * last-write-wins against the real settings row and came back down onto every
 * other device. The epoch instead makes a seed lose every comparison it takes
 * part in.
 */
export const SEED_TIMESTAMP = '1970-01-01T00:00:00.000Z';

/** Sync envelope for a seed row: oldest possible, and never pushed. */
export function seedMeta(): SyncMeta {
  return { updatedAt: SEED_TIMESTAMP, deletedAt: null, dirty: false };
}

/** True for a row still exactly as `createDefaults` left it. */
export function isSeedRow(row: SyncMeta): boolean {
  return row.updatedAt === SEED_TIMESTAMP && !row.dirty && row.deletedAt === null;
}

/**
 * What a brand-new install starts with: one task, one sticky note and three
 * lessons on the first week, matching the original app's `defaults`.
 */
export function createDefaults(): AppData {
  const firstWeek = SCHOOL_YEAR_START;

  return {
    settings: {
      teacher: 'Anso',
      className: 'P3',
      schoolYear: SCHOOL_YEAR_LABEL,
      ...seedMeta(),
    },
    students: [],
    tasks: [
      { id: uid(), text: 'Préparer ma semaine', done: false, due: '', ...seedMeta() },
    ],
    postits: [
      {
        id: uid(),
        text: 'Pense-bête : ajoute ici ce que tu ne veux pas oublier.',
        color: '#fff2b9',
        ...seedMeta(),
      },
    ],
    lessons: [
      {
        id: uid(),
        weekStart: firstWeek,
        day: 0,
        slot: 0,
        title: 'Accueil & présentation',
        detail: 'Règles de classe',
        subject: 'Français',
        domain: '',
        color: subjectColor('Français'),
        lessonNumber: '',
        competenceIds: [],
        competence: '',
        material: '',
        differentiation: '',
        ...seedMeta(),
      },
      {
        id: uid(),
        weekStart: firstWeek,
        day: 0,
        slot: 1,
        title: 'Distribution des fardes',
        detail: 'Organisation',
        subject: 'Formation manuelle, technique, technologique et numérique',
        domain: '',
        color: subjectColor('Formation manuelle, technique, technologique et numérique'),
        lessonNumber: '',
        competenceIds: [],
        competence: '',
        material: '',
        differentiation: '',
        ...seedMeta(),
      },
      {
        id: uid(),
        weekStart: firstWeek,
        day: 4,
        slot: 0,
        title: 'Anglais',
        detail: '',
        subject: 'Langues modernes',
        domain: '',
        color: subjectColor('Langues modernes'),
        lessonNumber: '',
        competenceIds: [],
        competence: '',
        material: '',
        differentiation: '',
        ...seedMeta(),
      },
    ],
    observations: [],
    evaluations: [],
    meetings: [],
    skills: [],
    weekTemplate: [],
  };
}
